import React, { useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { Trash2, LogIn, Mail, CheckCircle } from 'lucide-react';

export default function DataDeletionPage() {
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Data Deletion', path: '/data-deletion' }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  return (
    <div className={`min-h-screen ${theme.background}`}>
      {/* Header */}
      <div className={`${theme.background} py-4`}>
        <div className="max-w-3xl mx-auto px-4">
          <div className={`${theme.card} border ${theme.border} rounded-xl px-6 py-4 flex items-center gap-3`}>
            <Trash2 size={22} className="text-red-500" />
            <div>
              <p className={`text-lg font-bold leading-tight ${theme.text}`}>Request Account & Data Deletion</p>
              <p className={`text-xs leading-tight ${theme.textSecondary}`}>Kynaix Sports — Kynaix LLC</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* What gets deleted */}
        <div className={`${theme.card} border ${theme.border} rounded-xl p-6`}>
          <h2 className={`text-lg font-semibold ${theme.text} mb-4`}>What gets deleted</h2>
          <div className="space-y-3">
            {[
              { label: 'Account credentials', detail: 'Email, password, and login tokens — deleted within 30 days' },
              { label: 'Personal identifiers', detail: 'Name, username, profile photo, and contact information — deleted within 30 days' },
              { label: 'Profile data', detail: 'Bio, location, sports preferences, and skill level — deleted within 30 days' },
              { label: 'Uploaded documents', detail: 'Court permit PDFs and other uploaded files — deleted within 30 days' },
              { label: 'Push notification tokens', detail: 'Device tokens deregistered immediately' },
              { label: 'Financial records', detail: 'Group expense data deleted, subject to 7-year legal retention for transaction records' },
            ].map(({ label, detail }) => (
              <div key={label} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className={`text-sm font-medium ${theme.text}`}>{label}</p>
                  <p className={`text-xs ${theme.textSecondary}`}>{detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What is retained */}
        <div className={`${theme.card} border border-amber-400/40 rounded-xl p-6`}>
          <h2 className={`text-lg font-semibold ${theme.text} mb-2`}>What is retained after deletion</h2>
          <p className={`text-sm ${theme.textSecondary} mb-3`}>
            Sport participation records — games played, tournament results, scores, and match history — are retained permanently as part of the historical record of competitions you participated in. Your personal identifiers are removed and replaced with an anonymous identifier. Your name and contact information will not appear in these records.
          </p>
          <p className={`text-sm ${theme.textSecondary}`}>
            Posts and messages you created remain visible to other participants but your name and profile are replaced with "Anonymous User."
          </p>
        </div>

        {/* Option 1 — In-app */}
        <div className={`${theme.card} border ${theme.border} rounded-xl p-6`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
            <h2 className={`text-lg font-semibold ${theme.text}`}>Delete from within the app</h2>
          </div>
          <p className={`text-sm ${theme.textSecondary} mb-4`}>
            The fastest way. Log in to Kynaix Sports, go to your profile settings, and use the <strong>Delete Account</strong> option in the Danger Zone section. Your account and data will be permanently deleted within 30 days.
          </p>
          <a
            href="/profile/edit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors no-underline"
          >
            <LogIn className="w-4 h-4" />
            Go to Profile Settings
          </a>
        </div>

        {/* Option 2 — Email */}
        <div className={`${theme.card} border ${theme.border} rounded-xl p-6`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
            <h2 className={`text-lg font-semibold ${theme.text}`}>Request deletion by email</h2>
          </div>
          <p className={`text-sm ${theme.textSecondary} mb-3`}>
            If you no longer have access to the app or your account, send a deletion request to our privacy team. Include the email address associated with your Kynaix Sports account so we can locate and delete your data.
          </p>
          <div className={`${theme.backgroundSecondary} border ${theme.border} rounded-lg p-4 mb-4`}>
            <p className={`text-sm font-medium ${theme.text} mb-1`}>Email your request to:</p>
            <a
              href="mailto:support@kynaixsports.com?subject=Account%20Deletion%20Request&body=Please%20delete%20my%20Kynaix%20Sports%20account%20and%20all%20associated%20data.%0A%0AAccount%20email%3A%20"
              className={`text-sm font-medium ${theme.link} underline`}
            >
              support@kynaixsports.com
            </a>
            <p className={`text-xs ${theme.textSecondary} mt-1`}>Subject: "Account Deletion Request"</p>
          </div>
          <div className="flex items-start gap-2">
            <Mail className={`w-4 h-4 ${theme.textSecondary} flex-shrink-0 mt-0.5`} />
            <p className={`text-xs ${theme.textSecondary}`}>
              We will acknowledge your request within 5 business days and complete the deletion within 30 days. You will receive a confirmation email when deletion is complete.
            </p>
          </div>
        </div>

        {/* Footer links */}
        <div className={`text-center text-sm ${theme.textSecondary} pb-6`}>
          For more information about how we handle your data, see our{' '}
          <a href="/privacy" className={`${theme.link} underline`}>Privacy Policy</a>
          {' '}·{' '}
          <a href="/terms" className={`${theme.link} underline`}>Terms of Service</a>
        </div>

      </div>
    </div>
  );
}
